import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Eye,
  EyeOff,
  Landmark,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
  UserCog,
  UserRound,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Login = () => {
  const [role, setRole] = useState('member');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectedRoleMeta = useMemo(() => {
    if (role === 'admin') {
      return {
        title: 'Administrative Access',
        desc: 'Approve members, manage payroll deductions, process loans, run elections, and monitor reports.',
        icon: UserCog,
      };
    }

    return {
      title: 'Member Access',
      desc: 'View contributions, wallet balance, dividends, investments, loan status, and cooperative activities.',
      icon: UserRound,
    };
  }, [role]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    login(role, { username, password });

    if (role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/member');
    }
  };

  const RoleIcon = selectedRoleMeta.icon;

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowOne} />
      <div style={styles.bgGlowTwo} />

      <div
        style={{
          ...styles.layout,
          ...(isMobile ? styles.layoutMobile : {}),
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: isMobile ? 0 : -28, y: isMobile ? 18 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.45 }}
          style={{
            ...styles.leftPanel,
            ...(isMobile ? styles.leftPanelMobile : {}),
          }}
        >
          <div style={styles.brandRow}>
            <div style={styles.logoBadge}>
              <Landmark size={22} />
            </div>
            <div>
              <div style={styles.brandTitle}>FAAN Cooperative</div>
              <div style={styles.brandSub}>Smart Staff Cooperative Platform</div>
            </div>
          </div>

          <div style={styles.heroBadge}>
            <Sparkles size={14} />
            Secure digital cooperative experience
          </div>

          <h1
            style={{
              ...styles.heroTitle,
              ...(isMobile ? styles.heroTitleMobile : {}),
            }}
          >
            One modern portal for members, contributions, loans, dividends, and administration.
          </h1>

          <p style={styles.heroText}>
            Sign in to access your cooperative dashboard with a premium, bank-style
            experience designed for FAAN staff and administrators.
          </p>

          <div style={styles.featureGrid}>
            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <ShieldCheck size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Protected Access</div>
                <div style={styles.featureText}>
                  Role-based sign in for members and administrators.
                </div>
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <Building2 size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Cooperative Operations</div>
                <div style={styles.featureText}>
                  Savings, loans, payroll deductions, products, and elections.
                </div>
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconWrap}>
                <BadgeCheck size={18} />
              </div>
              <div>
                <div style={styles.featureTitle}>Fast Demo Access</div>
                <div style={styles.featureText}>
                  Use any demo username and password to enter the interface.
                </div>
              </div>
            </div>
          </div>

          <div style={styles.bottomInfoCard}>
            <div style={styles.bottomInfoTitle}>What you can do inside</div>
            <div style={styles.bottomInfoText}>
              Members can manage wallets, savings, dividends, and investments. Admins
              can approve members, run payroll reconciliation, process loans, manage
              products, and generate reports.
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: isMobile ? 0 : 28, y: isMobile ? 18 : 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          style={styles.rightPanel}
        >
          <div
            style={{
              ...styles.formShell,
              ...(isMobile ? styles.formShellMobile : {}),
            }}
          >
            <div style={styles.formTop}>
              <div>
                <div style={styles.formTitle}>Welcome back</div>
                <div style={styles.formSub}>
                  Sign in to continue to your cooperative workspace.
                </div>
              </div>
            </div>

            <div style={styles.roleTabs}>
              <button
                type="button"
                onClick={() => setRole('member')}
                style={{
                  ...styles.roleTab,
                  ...(role === 'member' ? styles.roleTabActive : {}),
                }}
              >
                <UserRound size={17} />
                Member
              </button>

              <button
                type="button"
                onClick={() => setRole('admin')}
                style={{
                  ...styles.roleTab,
                  ...(role === 'admin' ? styles.roleTabActive : {}),
                }}
              >
                <UserCog size={17} />
                Admin
              </button>
            </div>

            <motion.div
              key={role}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              style={styles.roleInfoCard}
            >
              <div style={styles.roleInfoIcon}>
                <RoleIcon size={18} />
              </div>
              <div>
                <div style={styles.roleInfoTitle}>{selectedRoleMeta.title}</div>
                <div style={styles.roleInfoText}>{selectedRoleMeta.desc}</div>
              </div>
            </motion.div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Username / Staff ID / Email</label>
                <div style={styles.inputWrap}>
                  <Mail size={18} color="#64748b" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={
                      role === 'admin'
                        ? 'Enter admin username'
                        : 'Enter staff ID or email'
                    }
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrap}>
                  <LockKeyhole size={18} color="#64748b" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    style={styles.input}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.eyeButton}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div
                style={{
                  ...styles.rowBetween,
                  ...(isMobile ? styles.rowBetweenMobile : {}),
                }}
              >
                <label style={styles.rememberWrap}>
                  <input type="checkbox" style={styles.checkbox} />
                  <span>Remember me</span>
                </label>

                <button type="button" style={styles.linkButton}>
                  Forgot password?
                </button>
              </div>

              <button type="submit" style={styles.submitButton}>
                Continue to Dashboard
                <ArrowRight size={17} />
              </button>
            </form>

            <div style={styles.demoHint}>
              Demo hint: any non-empty username and password will log you in.
            </div>

            <div style={styles.footerText}>
              Don&apos;t have an account?{' '}
              <Link to="/register" style={styles.footerLink}>
                Create member account
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top left, rgba(34,197,94,0.14), transparent 24%), linear-gradient(180deg, #f7fff9 0%, #f8fafc 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 18px',
  },
  bgGlowOne: {
    position: 'absolute',
    top: -120,
    right: -60,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'rgba(34,197,94,0.18)',
    filter: 'blur(80px)',
    pointerEvents: 'none',
  },
  bgGlowTwo: {
    position: 'absolute',
    bottom: -120,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'rgba(16,185,129,0.12)',
    filter: 'blur(90px)',
    pointerEvents: 'none',
  },
  layout: {
    width: '100%',
    maxWidth: 1260,
    display: 'grid',
    gridTemplateColumns: '1.1fr 0.9fr',
    gap: 22,
    position: 'relative',
    zIndex: 2,
    alignItems: 'stretch',
  },
  layoutMobile: {
    gridTemplateColumns: '1fr',
    gap: 18,
    maxWidth: 680,
  },
  leftPanel: {
    padding: 28,
    borderRadius: 30,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.96), rgba(15,23,42,0.95))',
    color: '#fff',
    boxShadow: '0 30px 70px rgba(5,150,105,0.22)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 720,
  },
  leftPanelMobile: {
    minHeight: 'unset',
    padding: 22,
    borderRadius: 24,
  },
  rightPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formShell: {
    width: '100%',
    maxWidth: 520,
    padding: 28,
    borderRadius: 30,
    background: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(15,23,42,0.06)',
    boxShadow: '0 24px 60px rgba(15,23,42,0.08)',
    backdropFilter: 'blur(16px)',
  },
  formShellMobile: {
    maxWidth: '100%',
    padding: 22,
    borderRadius: 24,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  logoBadge: {
    width: 48,
    height: 48,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.14)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandTitle: {
    fontSize: 18,
    fontWeight: 800,
  },
  brandSub: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.74)',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
    padding: '8px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.12)',
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 18,
  },
  heroTitle: {
    margin: 0,
    fontSize: 38,
    lineHeight: 1.15,
    fontWeight: 900,
    maxWidth: 720,
  },
  heroTitleMobile: {
    fontSize: 29,
    maxWidth: '100%',
  },
  heroText: {
    fontSize: 15,
    lineHeight: 1.75,
    color: 'rgba(255,255,255,0.82)',
    marginTop: 16,
    marginBottom: 24,
    maxWidth: 660,
  },
  featureGrid: {
    display: 'grid',
    gap: 14,
    marginBottom: 22,
  },
  featureCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  featureIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: 'rgba(255,255,255,0.14)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  featureTitle: {
    fontWeight: 800,
    fontSize: 15,
    marginBottom: 4,
  },
  featureText: {
    fontSize: 13.5,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.76)',
  },
  bottomInfoCard: {
    marginTop: 'auto',
    padding: 18,
    borderRadius: 22,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  bottomInfoTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 8,
  },
  bottomInfoText: {
    fontSize: 13.5,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.76)',
  },
  formTop: {
    marginBottom: 18,
  },
  formTitle: {
    fontSize: 30,
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 6,
  },
  formSub: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.6,
  },
  roleTabs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginBottom: 14,
  },
  roleTab: {
    height: 50,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontWeight: 800,
    color: '#334155',
    cursor: 'pointer',
  },
  roleTabActive: {
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    border: '1px solid transparent',
    boxShadow: '0 12px 26px rgba(22,163,74,0.18)',
  },
  roleInfoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 18,
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    border: '1px solid rgba(15,23,42,0.06)',
    marginBottom: 20,
  },
  roleInfoIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: '#ecfdf5',
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  roleInfoTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 4,
  },
  roleInfoText: {
    fontSize: 13.5,
    lineHeight: 1.6,
    color: '#64748b',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    color: '#334155',
  },
  inputWrap: {
    height: 54,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.10)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0 14px',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    color: '#0f172a',
    minWidth: 0,
  },
  eyeButton: {
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  rowBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  rowBetweenMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rememberWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#475569',
    fontSize: 13.5,
    fontWeight: 600,
  },
  checkbox: {
    accentColor: '#16a34a',
  },
  linkButton: {
    border: 'none',
    background: 'transparent',
    color: '#16a34a',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
  },
  submitButton: {
    height: 54,
    borderRadius: 16,
    border: 'none',
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    boxShadow: '0 14px 30px rgba(22,163,74,0.22)',
  },
  demoHint: {
    marginTop: 16,
    padding: 12,
    borderRadius: 14,
    background: '#f8fafc',
    color: '#64748b',
    fontSize: 12.8,
    lineHeight: 1.6,
    border: '1px solid rgba(15,23,42,0.05)',
  },
  footerText: {
    marginTop: 16,
    textAlign: 'center',
    fontSize: 14,
    color: '#64748b',
  },
  footerLink: {
    color: '#16a34a',
    fontWeight: 800,
    textDecoration: 'none',
  },
};

export default Login;